// The store behind the unread badges: a read cursor and an unread count per
// channel, for the current user and site.
//
// The server owns read state (a per-channel cursor): `seedFromServer` takes
// the authoritative counts from the channel-list response so counts stay
// consistent across a user's devices. The device cache is just a first-paint
// cache here; the live WS increment + open-channel reset run between server
// snapshots.
//
// Where the cache lives is the app's (`IUnreadStorage`). A storage that can
// read synchronously (localStorage) fills the store before the first render.
// One that cannot (AsyncStorage) starts the store empty and loads the cache
// in an effect; a channel touched live before the cache lands (a read, a
// socket increment, a server seed) keeps its live state, and the cache only
// fills in channels nothing has touched yet. `hydrated` tells a list when the
// first paint is safe — it is true at once with a synchronous storage.

import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

const STORAGE_PREFIX_LAST_READ = 'comms:lastRead:v1';
const STORAGE_PREFIX_COUNT = 'comms:unreadCount:v1';

type StringMap = Record<string, string>;
type NumberMap = Record<string, number>;

/** The device cache, as the app provides it. */
export interface IUnreadStorage {
  /** Read now, when the platform can: the store then starts full instead of loading. */
  getSync?: (key: string) => string | null;
  get: (key: string) => Promise<string | null>;
  set: (key: string, value: string) => void | Promise<void>;
}

function lastReadKey(userId: string, siteId: string): string {
  return `${STORAGE_PREFIX_LAST_READ}:${userId}:${siteId}`;
}

function countKey(userId: string, siteId: string): string {
  return `${STORAGE_PREFIX_COUNT}:${userId}:${siteId}`;
}

function parseStringMap(raw: string | null): StringMap {
  try {
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as StringMap;
    }
    return {};
  } catch {
    return {};
  }
}

function parseNumberMap(raw: string | null): NumberMap {
  try {
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      // Coerce non-numeric noise to 0 so a hand-edited value can't blow up callers.
      const out: NumberMap = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) out[k] = v;
      }
      return out;
    }
    return {};
  } catch {
    return {};
  }
}

/** A storage that cannot read now, or throws on read (a private window), reads as empty. */
function readNow(storage: IUnreadStorage, key: string): string | null {
  try {
    return storage.getSync?.(key) ?? null;
  } catch {
    return null;
  }
}

function write(storage: IUnreadStorage, key: string, value: unknown): void {
  // Quota exceeded / storage unavailable — in-memory state still works,
  // only persistence is lost.
  try {
    const result = storage.set(key, JSON.stringify(value));
    if (typeof (result as Promise<void> | undefined)?.then === 'function') (result as Promise<void>).catch(() => {});
  } catch {
    // see above
  }
}

export interface IUnreadValue {
  lastReadMap: StringMap;
  countMap: NumberMap;
  /** False until the device cache has been read once for the current user + site. */
  hydrated: boolean;
  markRead: (channelId: string, atIso?: string) => void;
  incrementUnread: (channelId: string) => void;
  /** Authoritative server snapshot (from the channel-list response). Overwrites
   *  local counts; skips `activeChannelId` (open = 0); advances cursors forward only. */
  seedFromServer: (
    entries: Array<{ channelId: string; unreadCount: number; lastReadAt?: string }>,
    activeChannelId: string | null,
  ) => void;
}

const UnreadContext = createContext<IUnreadValue | null>(null);

export interface IUnreadProviderProps {
  userId: string | null;
  siteId: string | null;
  storage: IUnreadStorage;
  children: ReactNode;
}

export function UnreadProvider({ userId, siteId, storage, children }: IUnreadProviderProps) {
  const lastReadKeyStr = userId && siteId ? lastReadKey(userId, siteId) : null;
  const countKeyStr = userId && siteId ? countKey(userId, siteId) : null;
  const readsNow = !!storage.getSync;

  // A storage that reads now fills the store before the first render; one
  // that cannot starts empty and is loaded below.
  const [lastReadMap, setLastReadMap] = useState<StringMap>(() =>
    readsNow && lastReadKeyStr ? parseStringMap(readNow(storage, lastReadKeyStr)) : {},
  );
  const [countMap, setCountMap] = useState<NumberMap>(() =>
    readsNow && countKeyStr ? parseNumberMap(readNow(storage, countKeyStr)) : {},
  );
  // Which cache key has been loaded; nothing to load without a user + site.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const hydrated = readsNow || !lastReadKeyStr || loadedKey === lastReadKeyStr;

  // Keep latest keys in refs so callbacks don't have to re-bind on every render.
  const lastReadKeyRef = useRef(lastReadKeyStr);
  const countKeyRef = useRef(countKeyStr);
  useEffect(() => {
    lastReadKeyRef.current = lastReadKeyStr;
    countKeyRef.current = countKeyStr;
  }, [lastReadKeyStr, countKeyStr]);

  // Mirror the latest read cursors so seedFromServer can compare server vs
  // local read state without re-binding the callback on every cursor change.
  const lastReadMapRef = useRef(lastReadMap);
  useEffect(() => {
    lastReadMapRef.current = lastReadMap;
  }, [lastReadMap]);

  // Channels touched live before the cache landed — the cache must not
  // overwrite them.
  const touchedRef = useRef(new Set<string>());

  // Load the device cache once per user + site, filling in only what nothing
  // has touched while the read was in flight.
  useEffect(() => {
    if (readsNow || !lastReadKeyStr || !countKeyStr) return;
    let cancelled = false;
    void Promise.all([storage.get(lastReadKeyStr), storage.get(countKeyStr)])
      .then(([rawCursors, rawCounts]) => [parseStringMap(rawCursors), parseNumberMap(rawCounts)] as const)
      .catch(() => [{}, {}] as const)
      .then(([storedCursors, storedCounts]) => {
        if (cancelled) return;
        const touched = touchedRef.current;
        setLastReadMap((prev) => {
          const next = { ...prev };
          for (const [channelId, stamp] of Object.entries(storedCursors)) {
            if (!touched.has(channelId)) next[channelId] = stamp;
          }
          return next;
        });
        setCountMap((prev) => {
          const next = { ...prev };
          for (const [channelId, count] of Object.entries(storedCounts)) {
            if (!touched.has(channelId)) next[channelId] = count;
          }
          return next;
        });
        setLoadedKey(lastReadKeyStr);
      });
    return () => {
      cancelled = true;
    };
  }, [readsNow, storage, lastReadKeyStr, countKeyStr]);

  const markRead = useCallback((channelId: string, atIso?: string) => {
    const stamp = atIso ?? new Date().toISOString();
    touchedRef.current.add(channelId);
    setLastReadMap((prev) => {
      const existing = prev[channelId];
      if (existing && existing >= stamp) return prev;
      const next = { ...prev, [channelId]: stamp };
      if (lastReadKeyRef.current) write(storage, lastReadKeyRef.current, next);
      return next;
    });
    setCountMap((prev) => {
      if (!prev[channelId]) return prev;
      const next = { ...prev };
      delete next[channelId];
      if (countKeyRef.current) write(storage, countKeyRef.current, next);
      return next;
    });
  }, [storage]);

  const incrementUnread = useCallback((channelId: string) => {
    touchedRef.current.add(channelId);
    setCountMap((prev) => {
      const next = { ...prev, [channelId]: (prev[channelId] ?? 0) + 1 };
      if (countKeyRef.current) write(storage, countKeyRef.current, next);
      return next;
    });
  }, [storage]);

  const seedFromServer = useCallback(
    (
      entries: Array<{ channelId: string; unreadCount: number; lastReadAt?: string }>,
      activeChannelId: string | null,
    ) => {
      const localCursors = lastReadMapRef.current;
      for (const { channelId } of entries) touchedRef.current.add(channelId);
      setCountMap((prev) => {
        const next = { ...prev };
        for (const { channelId, unreadCount, lastReadAt } of entries) {
          if (channelId === activeChannelId) {
            delete next[channelId];
            continue;
          }
          // If the server's read cursor advanced past our local one, the channel
          // was read on another device — trust the server count. Otherwise our
          // local count may include live WS increments that arrived while this
          // fetch was in flight, so keep whichever is larger rather than letting
          // a stale snapshot clobber them.
          const readElsewhere = !!lastReadAt && lastReadAt > (localCursors[channelId] ?? '');
          const merged = readElsewhere ? unreadCount : Math.max(unreadCount, prev[channelId] ?? 0);
          if (merged > 0) next[channelId] = merged;
          else delete next[channelId];
        }
        if (countKeyRef.current) write(storage, countKeyRef.current, next);
        return next;
      });
      setLastReadMap((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const { channelId, lastReadAt } of entries) {
          // Advance forward only — a local just-read cursor must not jump back.
          if (lastReadAt && (!next[channelId] || lastReadAt > next[channelId])) {
            next[channelId] = lastReadAt;
            changed = true;
          }
        }
        if (changed && lastReadKeyRef.current) write(storage, lastReadKeyRef.current, next);
        return changed ? next : prev;
      });
    },
    [storage],
  );

  const value = useMemo<IUnreadValue>(
    () => ({ lastReadMap, countMap, hydrated, markRead, incrementUnread, seedFromServer }),
    [lastReadMap, countMap, hydrated, markRead, incrementUnread, seedFromServer],
  );

  // `createElement`, not JSX: the shared package compiles plain `.ts` only.
  return createElement(UnreadContext.Provider, { value }, children);
}

// The hook is colocated with its provider on purpose — they share UnreadContext.
export function useUnread(): IUnreadValue {
  const ctx = useContext(UnreadContext);
  if (!ctx) throw new Error('useUnread must be used inside <UnreadProvider>');
  return ctx;
}
